import { Location } from '@angular/common';
import { Component, ElementRef, inject, Injectable, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PasInputFieldComponent, PasSpinnerComponent } from '@porsche-aftersales/frontend-toolbox';

import { BookingApprovalsHistoryModel } from '../../content/recourse-booking/model/booking-approvals-history-model';
import { BookingService } from '../../services/booking/booking.service';
import { RecourseBookingStore } from '../../services/booking/recourse-booking-store';
import { LoggerService } from '../../services/logging-service/logger.service';
import { NotificationServiceWrapper } from '../../services/notification-service-wrapper/notification-service-wrapper';
import { GenericModalContentComponent } from '../generic-modal-content/generic-modal-content.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'approve-reject-reprocess-dialog',
  templateUrl: './approve-reject-reprocess-dialog.component.html',
  imports: [PasSpinnerComponent, GenericModalContentComponent, TranslatePipe, PasInputFieldComponent, FormsModule],
})
@Injectable({
  providedIn: 'root',
})
export class ApproveRejectReprocessDialog {
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly modalService = inject(NgbModal);
  private readonly translateService = inject(TranslateService);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly loggerService = inject(LoggerService);
  private readonly bookingService = inject(BookingService);
  private readonly location = inject(Location);

  private static readonly CONST_APPROVED = 'approved';
  private static readonly CONST_REJECTED = 'rejected';

  @ViewChild('ApproveDialogTemplate') public approvalDialogTemplate: ElementRef;
  public approvalDialog: NgbModalRef;

  @ViewChild('RejectApprovalDialogTemplate') public rejectApprovalDialogTemplate: ElementRef;
  public rejectApprovalDialog: NgbModalRef;
  public rejectComment = '';

  @ViewChild('ReprocessDialogTemplate') public reprocessDialogTemplate: ElementRef;
  private reprocessDialog: NgbModalRef;

  public spinnerActive = false;
  public pkBookingId: number;

  public bookingApprovalsHistory: BookingApprovalsHistoryModel[] = [];
  public bookingApprovalsHistoryAccordionClosed = true;

  public onAcceptApproveDialog(): void {
    this.approveRecourseBooking(true, ApproveRejectReprocessDialog.CONST_APPROVED);
  }

  public onAcceptRejectApprovalDialog(): void {
    this.approveRecourseBooking(false, ApproveRejectReprocessDialog.CONST_REJECTED);
  }

  public onAcceptReprocessDialog(): void {
    this.reprocessRecourseBooking();
  }

  public onCloseApproveDialog(): void {
    this.approvalDialog.close(false);
  }

  public onCloseRejectApprovalDialog(): void {
    this.rejectComment = '';
    this.rejectApprovalDialog.close(false);
  }

  public onCloseReprocessDialog(): void {
    this.reprocessDialog.close(false);
  }

  public async onBtnApproveBooking(pkBookingId?: number): Promise<boolean> {
    // open dialog window
    let returnValue = false;
    this.pkBookingId = pkBookingId;
    this.approvalDialog = this.openDialogTemplate(this.approvalDialogTemplate);
    // this handles actions after dialog is closed
    await this.approvalDialog.result.then(result => {
      returnValue = result;
    });
    return returnValue;
  }

  public async onBtnRejectBooking(pkBookingId?: number): Promise<boolean> {
    // open dialog window
    let returnValue = false;
    this.pkBookingId = pkBookingId;
    this.rejectApprovalDialog = this.openDialogTemplate(this.rejectApprovalDialogTemplate);
    // this handles actions after dialog is closed
    await this.rejectApprovalDialog.result.then(result => {
      returnValue = result;
    });
    return returnValue;
  }

  public async onBtnReprocessBooking(): Promise<boolean> {
    // open dialog window
    let returnValue = false;
    this.reprocessDialog = this.openDialogTemplate(this.reprocessDialogTemplate);
    // this handles actions after dialog is closed
    await this.reprocessDialog.result.then(result => {
      returnValue = result;
    });
    return returnValue;
  }

  private openDialogTemplate(dialogTemplate: ElementRef): NgbModalRef {
    return this.modalService.open(dialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });
  }

  private approveRecourseBooking(approve: boolean, callerType: string): void {
    this.spinnerActive = true;
    const selectedBooking = this.recourseBookingStore.selectedRecourseBooking;
    const bookingId = this.pkBookingId ?? selectedBooking.pkBookingId;
    this.bookingService.approveOrRejectBooking(bookingId, approve, this.rejectComment).subscribe({
      next: () => {
        this.spinnerActive = false;
        switch (callerType) {
          case ApproveRejectReprocessDialog.CONST_REJECTED: {
            this.rejectApprovalDialog.close(true);
            this.showRejectSuccessMessage(bookingId);
            break;
          }
          case ApproveRejectReprocessDialog.CONST_APPROVED: {
            this.approvalDialog.close(true);
            this.showApproveSuccessMessage(bookingId);
            break;
          }
          // no default
        }
      },
      error: err => {
        this.spinnerActive = false;
        switch (callerType) {
          case ApproveRejectReprocessDialog.CONST_REJECTED: {
            this.showRejectFailedMessage(bookingId, err);
            break;
          }
          case ApproveRejectReprocessDialog.CONST_APPROVED: {
            this.showApproveFailedMessage(bookingId, err);
            break;
          }
          // no default
        }
      },
    });
  }

  private reprocessRecourseBooking(): void {
    this.spinnerActive = true;
    const selectedBooking = this.recourseBookingStore.selectedRecourseBooking;
    const bookingId = this.pkBookingId ?? selectedBooking.pkBookingId;
    this.bookingService.reprocessBooking(bookingId).subscribe({
      next: () => {
        // On reprocessing the history/BookingApprovals gets not updated because no new entry will be added on the table.
        this.spinnerActive = false;
        if (this.reprocessDialog) {
          this.reprocessDialog.close(true);
          this.showReprocessSuccessMessage(bookingId);
          this.onBtnCancel();
        }
      },
      error: err => {
        this.spinnerActive = false;
        if (this.reprocessDialog) {
          this.reprocessDialog.close(true);
          this.showReprocessFailedMessage(bookingId, err);
        }
      },
    });
  }

  public onBtnCancel(): void {
    this.location.back();
  }

  private showReprocessSuccessMessage(bookingId: any): void {
    this.showSuccessMessage(bookingId, 'TITLE_REPROCESS', 'REPROCESS_BOOKING_SUCCESSFUL');
  }

  private showReprocessFailedMessage(bookingId: any, error: any): void {
    this.showFailureMessage(bookingId, error, 'reprocess', 'TITLE_FAILED', 'REPROCESS_BOOKING_FAILED');
  }

  private showApproveSuccessMessage(bookingId: any): void {
    this.showSuccessMessage(bookingId, 'TITLE_APPROVAL', 'APPROVAL_BOOKING_SUCCESSFUL');
  }

  private showApproveFailedMessage(bookingId: any, error: any): void {
    this.showFailureMessage(bookingId, error, 'approve', 'TITLE_FAILED', 'APPROVAL_BOOKING_FAILED');
  }

  private showRejectSuccessMessage(bookingId: any): void {
    this.showSuccessMessage(bookingId, 'TITLE_REJECT', 'REJECT_BOOKING_SUCCESSFUL');
  }

  private showRejectFailedMessage(bookingId: any, error: any): void {
    this.showFailureMessage(bookingId, error, 'reject', 'TITLE_FAILED', 'REJECT_BOOKING_FAILED');
  }

  private showSuccessMessage(bookingId: any, messageTitle: string, messageContent: string): void {
    this.translateService.get([messageTitle, messageContent]).subscribe((translate: { [key: string]: any }) => {
      const title: string = translate[messageTitle];
      const content: string = this.translateService.instant(messageContent, { 0: bookingId });
      this.notificationServiceWrapper.addSuccessNotification(title, content);
    });
  }

  private showFailureMessage(
    bookingId: any,
    error: any,
    action: string,
    messageTitle: string,
    messageContent: string,
  ): void {
    this.loggerService.error(`Failed to ${action} recourse booking:${error.toString()}`);
    this.translateService.get([messageTitle, messageContent]).subscribe((translate: { [key: string]: any }) => {
      const title: string = translate[messageTitle];
      const content: string = this.translateService.instant(messageContent, { 0: bookingId });
      this.notificationServiceWrapper.addErrorNotification(title, content);
    });
  }
}
